import { Tag } from 'antd';
import type { FleetDevice } from './model';
import { supportsMonitoring } from './model';

export const statusLabels = { active: 'Aktiv', inactive: 'Inaktiv', unknown: 'Ukendt' };
export const reviewLabels = {
  pending: 'Til vurdering',
  investigating: 'Undersøges',
  reviewed: 'Vurderet',
};

export function formatDate(value: Date | number | null | undefined) {
  if (value == null || !Number.isFinite(new Date(value).getTime())) return 'Intet tidspunkt';
  return new Intl.DateTimeFormat('da-DK', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Copenhagen',
  }).format(value);
}

export function HealthBadge({
  device,
  unavailable = false,
  loading = false,
}: {
  device: FleetDevice;
  unavailable?: boolean;
  loading?: boolean;
}) {
  if (!supportsMonitoring(device.type)) return <Tag>Ikke centralt overvåget</Tag>;
  if (unavailable) return <Tag color="gold">Overvågning utilgængelig</Tag>;
  if (loading) return <Tag>Henter status…</Tag>;
  if (!device.monitoring) return <Tag>Afventer overvågning</Tag>;
  const health = device.monitoring.currentHealth;
  const labels = {
    healthy: 'Ingen åbne alarmer',
    warning: 'Advarsel',
    critical: 'Kritisk',
    inactive: 'Overvågning inaktiv',
  };
  const colors = { healthy: 'green', warning: 'gold', critical: 'red', inactive: undefined };
  return <Tag color={colors[health]}>{labels[health] || 'Ukendt helbred'}</Tag>;
}

export function escapeCsvValue(value: unknown) {
  const text = value == null ? '' : String(value);
  // Spreadsheet apps must treat device names and IDs as data, including formula-like names.
  const safe = /^[\s]*[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function exportFleetCsv(devices: FleetDevice[]) {
  const rows = [
    ['Kunde', 'Kunde-ID', 'Enhed', 'Sensor-ID', 'Type', 'Registrering', 'Device-ID'],
    ...devices.map((device) => [
      device.orgName,
      device.orgId,
      device.sensorName,
      device.sensorId,
      device.type,
      statusLabels[device.status],
      device.deviceId,
    ]),
  ];
  const csv = `\uFEFFsep=,\r\n${rows.map((row) => row.map(escapeCsvValue).join(',')).join('\r\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `enheder-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
